import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
  computed,
  Renderer2,
  PLATFORM_ID,
  ChangeDetectionStrategy
} from '@angular/core';
import { RouterLink, RouterModule } from '@angular/router';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';

import { MegaMenuEquipmentsComponent } from '../mega-menu-equipments/mega-menu-equipments.component';
import { NewEquipmentService } from '../../services/new-equipment.service';
import { UsedEquipmentService } from '../../services/used-equipment.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage, RouterLink, MegaMenuEquipmentsComponent, RouterModule],
})
export class HeaderComponent {
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);
  private newEquipmentService = inject(NewEquipmentService);
  private usedEquipmentService = inject(UsedEquipmentService);

  private readonly categoriesData = toSignal(this.newEquipmentService.getCategories(0, 0, ''));
  readonly newCategories = computed(() => this.categoriesData()?.results ?? []);
  private readonly usedCategoriesData = toSignal(this.usedEquipmentService.getAllCategories());
  readonly usedCategories = computed(() => this.usedCategoriesData()?.results ?? []);
  readonly mobileSubPanel = signal<'new-equipment' | 'used-equipment' | null>(null);

  @ViewChild('menuMobileLinks', { static: false })
  menuLinks?: ElementRef<HTMLElement>;

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /* ==============================
     CLICK FUERA DEL MENU MOBILE
     ============================== */
  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: MouseEvent): void {
    if (!this.isBrowser) return;

    const target = event.target as HTMLElement;
    const menuMobile = document.getElementById('menuMobile');

    if (menuMobile && menuMobile.contains(target)) return;

    this.closeMobileMenu();
    this.closeMobileDropdown();
  }

  /* ==============================
     MEGA MENU DESKTOP
     ============================== */
  toggleMegaMenuEquipments(): void {
    const mainMenu = document.getElementById('mainMenu');
    const dropdown = document.getElementById('megaMenuEquipos');
    const megaMenuEquipments = document.getElementById('megaMenuEquipments');

    const isOpen = megaMenuEquipments?.classList.contains('open');

    if (isOpen) {
      this.renderer.removeClass(mainMenu, 'blurred');
      this.renderer.removeClass(dropdown, 'open');
      this.renderer.removeClass(megaMenuEquipments, 'open');
    } else {
      this.renderer.addClass(mainMenu, 'blurred');
      this.renderer.addClass(dropdown, 'open');
      this.renderer.addClass(megaMenuEquipments, 'open');
    }
  }

  checkHover(): void {
    if (!this.isBrowser || window.innerWidth <= 992) return;

    const mainMenu = document.getElementById('mainMenu');
    const dropdown = document.getElementById('megaMenuEquipos');
    const megaMenu = document.getElementById('megaMenuEquipments');

    if (!megaMenu?.matches(':hover')) {
      [dropdown, megaMenu].forEach((el) => el && this.renderer.removeClass(el, 'open'));
      if (mainMenu) {
        this.renderer.removeClass(mainMenu, 'blurred');
      }
    }
  }

  /* ==============================
     MENU MOBILE
     ============================== */
  toggleMenuMobile(): void {
    if (!this.isBrowser) return;

    const menu = document.getElementById('menuMobile');
    const icon = document.querySelector('.navLinksMenu');

    if (!menu || !icon) return;

    const isOpen = icon.classList.contains('open');

    const mainMenu = document.getElementById('mainMenu');
    this.renderer[isOpen ? 'removeClass' : 'addClass'](menu, 'show');
    this.renderer[isOpen ? 'removeClass' : 'addClass'](icon, 'open');
    this.renderer[isOpen ? 'removeClass' : 'addClass'](mainMenu, 'menu-open');
    if (isOpen) {
      this.renderer.removeStyle(document.body, 'overflow');
    } else {
      this.renderer.setStyle(document.body, 'overflow', 'hidden');
    }
  }

  toggleDropdownMobile(): void {
    if (!this.isBrowser) return;

    const dropdown = document.querySelector('#menuMobile .dropdown');
    const pages = document.querySelector('#menuMobile .pages');

    const isActive = dropdown?.classList.contains('active');

    [dropdown, pages].forEach(
      (el) => el && this.renderer[isActive ? 'removeClass' : 'addClass'](el, 'active')
    );
  }

  /* ==============================
     HELPERS
     ============================== */
  openMobileSubPanel(panel: 'new-equipment' | 'used-equipment'): void {
    this.mobileSubPanel.set(panel);
  }

  closeMobileSubPanel(): void {
    this.mobileSubPanel.set(null);
  }

  private closeMobileMenu(): void {
    const menu = document.getElementById('menuMobile');
    const icon = document.querySelector('.navLinksMenu');

    if (menu && icon) {
      const mainMenu = document.getElementById('mainMenu');
      this.renderer.removeClass(menu, 'show');
      this.renderer.removeClass(icon, 'open');
      this.renderer.removeClass(mainMenu, 'menu-open');
      this.mobileSubPanel.set(null);
      this.renderer.removeStyle(document.body, 'overflow');
    }
  }

  private closeMobileDropdown(): void {
    const dropdown = document.querySelector('#menuMobile .dropdown');
    const pages = document.querySelector('#menuMobile .pages');

    [dropdown, pages].forEach((el) => el && this.renderer.removeClass(el, 'active'));
  }
}
